/**
 * SkillManager Component
 * 
 * Provides a UI for managing the skill taxonomy hierarchy.
 * Features:
 * - Tree view of skill hierarchy
 * - CRUD operations for skills
 * - Visual depth indicators
 * - Search/filter capabilities
 */

import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import {
  skillService,
  SkillParameter,
  SkillTreeNode,
  SkillParameterCreate,
  SkillParameterUpdate,
  SkillImportResponse
} from '../../services/skillService';
import styles from './SkillManager.module.css';

interface SkillManagerProps {
  onSkillSelect?: (skill: SkillParameter) => void;
}

export const SkillManager: React.FC<SkillManagerProps> = ({ onSkillSelect }) => {
  const [skillTree, setSkillTree] = useState<SkillTreeNode[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [selectedSkill, setSelectedSkill] = useState<SkillParameter | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<SkillImportResponse | null>(null);
  const [overwriteExisting, setOverwriteExisting] = useState(false);

  const [formData, setFormData] = useState<SkillParameterCreate>({
    name: '',
    description: '',
    parent_ref: undefined
  });

  useEffect(() => {
    void loadSkillTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Transform production backend response (category-path-keyed object) to tree structure
  const transformProductionTreeResponse = (data: any): SkillTreeNode[] => {
    if (!data || typeof data !== 'object') {
      return [];
    }

    // If it's already in the expected format
    if (data.roots && Array.isArray(data.roots)) {
      return data.roots;
    }

    // If it's an array, try to build tree from it
    if (Array.isArray(data)) {
      // Check if array items have name fields with hierarchy
      const hasHierarchyInName = data.length > 0 && 
        data[0] && 
        typeof data[0] === 'object' && 
        (((data[0] as any).name && (data[0] as any).name.includes(' > ')) ||
        ((data[0] as any).skill_name && (data[0] as any).skill_name.includes(' > ')));
      
      if (hasHierarchyInName) {
        // Build tree from array where name contains hierarchy
        const nodeMap = new Map<string, SkillTreeNode>();
        const pathToNodeMap = new Map<string, SkillTreeNode>();
        const rootNodes: SkillTreeNode[] = [];
        
        // First pass: create all nodes
        for (const item of data) {
          if (!item || typeof item !== 'object') continue;
          
          const nameField = (item as any).name || (item as any).skill_name || '';
          if (!nameField || !nameField.includes(' > ')) continue;
          
          const pathParts = nameField.split(' > ');
          const skillName = pathParts[pathParts.length - 1];
          const skillId = (item as any).id || (item as any).skill_id || nameField;
          
          const node: SkillTreeNode = {
            id: String(skillId),
            name: skillName,
            description: (item as any).description || (item as any).skill_description,
            skill_level: (item as any).skill_level !== undefined ? (item as any).skill_level : pathParts.length - 1,
            parent_ref: undefined,
            children: [],
            ...(item as any)
          };
          
          nodeMap.set(String(skillId), node);
          pathToNodeMap.set(nameField, node);
        }
        
        // Second pass: build hierarchy
        const pathEntries = Array.from(pathToNodeMap.entries());
        for (const [hierarchyPath, node] of pathEntries) {
          const pathParts = hierarchyPath.split(' > ');
          if (pathParts.length === 1) {
            rootNodes.push(node);
          } else {
            const parentPath = pathParts.slice(0, -1).join(' > ');
            const parentNode = pathToNodeMap.get(parentPath);
            if (parentNode) {
              node.parent_ref = parentNode.id;
              parentNode.children.push(node);
            } else {
              rootNodes.push(node);
            }
          }
        }
        
        // Sort nodes
        const sortNodes = (nodes: SkillTreeNode[]) => {
          nodes.sort((a, b) => a.name.localeCompare(b.name));
          nodes.forEach(node => {
            if (node.children.length > 0) {
              sortNodes(node.children);
            }
          });
        };
        sortNodes(rootNodes);
        return rootNodes;
      }
      
      // Otherwise, return as-is (might already be in tree format)
      return data;
    }

    // Production backend returns object with category paths as keys
    // e.g., { "Analytical & Diagnostic > Data Collection": { skill_id: "...", skill_name: "...", ... } }
    // OR the name field itself contains the hierarchy: { name: "Analytical & Diagnostic > Data Collection", ... }
    const rootNodes: SkillTreeNode[] = [];
    const nodeMap = new Map<string, SkillTreeNode>();
    const categoryToNodeMap = new Map<string, SkillTreeNode>();

    // First pass: create all nodes and build category hierarchy
    for (const [categoryPath, skillData] of Object.entries(data)) {
      if (!skillData || typeof skillData !== 'object') continue;

      // Log first entry to understand structure
      if (categoryToNodeMap.size === 0) {
        console.log('Sample skill data structure:', { categoryPath, skillData });
      }

      // Extract skill data - handle both production and local backend formats
      // Production backend might have skill_id, id, or the data might be nested
      let skillId = (skillData as any).skill_id || (skillData as any).id;
      
      // If no skill_id found, try nested structures
      if (!skillId) {
        skillId = (skillData as any).skill?.id || (skillData as any).skill?.skill_id;
      }
      
      // Extract name - check if it contains hierarchy (has '>')
      let skillName = (skillData as any).skill_name || (skillData as any).name || (skillData as any).skill?.name || '';
      
      // If name contains '>', it's the full hierarchy path
      // Use the categoryPath as the hierarchy source, or parse from name
      let hierarchyPath = categoryPath;
      if (skillName && skillName.includes(' > ')) {
        // Name field contains the hierarchy - use it as the source
        hierarchyPath = skillName;
        // Extract just the skill name (last part after '>')
        skillName = skillName.split(' > ').pop() || skillName;
      } else if (!skillName) {
        // Fallback: extract from categoryPath
        skillName = categoryPath.split(' > ').pop() || categoryPath;
      }
      
      // If still no UUID, use hierarchy path as ID (we'll handle this in click handler)
      if (!skillId) {
        skillId = hierarchyPath;
      }
      
      // Calculate skill level from hierarchy depth
      const pathParts = hierarchyPath.split(' > ');
      const skillLevel = (skillData as any).skill_level !== undefined 
        ? (skillData as any).skill_level 
        : ((skillData as any).skill?.skill_level !== undefined 
          ? (skillData as any).skill.skill_level 
          : pathParts.length - 1);
      
      const description = (skillData as any).skill_description || (skillData as any).description || (skillData as any).skill?.description;
      
      // Store the full skill data in the node for later use
      const node: SkillTreeNode = {
        id: String(skillId),
        name: String(skillName),
        description: description,
        skill_level: Number(skillLevel),
        parent_ref: undefined,
        children: [],
        // Store original data for reference
        ...(skillData as any)
      };

      nodeMap.set(String(skillId), node);
      categoryToNodeMap.set(hierarchyPath, node);
    }

    // Second pass: build hierarchy from category paths
    const categoryEntries = Array.from(categoryToNodeMap.entries());
    for (const [hierarchyPath, node] of categoryEntries) {
      const pathParts = hierarchyPath.split(' > ');
      
      if (pathParts.length === 1) {
        // Root level node
        rootNodes.push(node);
      } else {
        // Find parent by building parent path
        const parentPath = pathParts.slice(0, -1).join(' > ');
        const parentNode = categoryToNodeMap.get(parentPath);
        
        if (parentNode) {
          node.parent_ref = parentNode.id;
          parentNode.children.push(node);
        } else {
          // Parent doesn't exist in the map, create intermediate parent or make it a root
          // Try to find a parent by matching partial paths
          let foundParent = false;
          for (let i = pathParts.length - 2; i >= 0; i--) {
            const partialPath = pathParts.slice(0, i + 1).join(' > ');
            const potentialParent = categoryToNodeMap.get(partialPath);
            if (potentialParent) {
              node.parent_ref = potentialParent.id;
              potentialParent.children.push(node);
              foundParent = true;
              break;
            }
          }
          
          if (!foundParent) {
            // No parent found, make it a root
            rootNodes.push(node);
          }
        }
      }
    }

    // Sort nodes by name for consistent display
    const sortNodes = (nodes: SkillTreeNode[]) => {
      nodes.sort((a, b) => a.name.localeCompare(b.name));
      nodes.forEach(node => {
        if (node.children.length > 0) {
          sortNodes(node.children);
        }
      });
    };

    sortNodes(rootNodes);

    return rootNodes;
  };

  const loadSkillTree = async () => {
    setLoading(true);
    setError(null);
    try {
      const tree = await skillService.getSkillTree();
      console.log('Skill tree response:', tree);
      console.log('Skill tree response type:', typeof tree);
      console.log('Is array?', Array.isArray(tree));
      console.log('Has roots?', tree && typeof tree === 'object' && 'roots' in tree);
      
      // Log first few entries if it's an object
      if (tree && typeof tree === 'object' && !Array.isArray(tree) && !('roots' in tree)) {
        const entries = Object.entries(tree);
        console.log('First 3 entries:', entries.slice(0, 3));
        if (entries.length > 0) {
          const [firstKey, firstValue] = entries[0];
          console.log('First entry key:', firstKey);
          console.log('First entry value:', firstValue);
          console.log('First entry value keys:', firstValue && typeof firstValue === 'object' ? Object.keys(firstValue) : 'N/A');
        }
      }
      
      // Transform the response to the expected format
      const transformedTree = transformProductionTreeResponse(tree);
      console.log('Transformed tree:', transformedTree);
      console.log('Transformed tree root count:', transformedTree.length);
      if (transformedTree.length > 0) {
        console.log('First root node:', transformedTree[0]);
        if (transformedTree[0].children && transformedTree[0].children.length > 0) {
          console.log('First root has children:', transformedTree[0].children.length);
          console.log('First child:', transformedTree[0].children[0]);
        }
      }
      
      setSkillTree(transformedTree);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to load skill tree';
      setError(`Failed to load skill tree: ${errorMessage}`);
      console.error('Error loading skill tree:', err);
      console.error('Error response:', err?.response?.data);
      setSkillTree([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleNodeClick = async (nodeId: string) => {
    try {
      // Check if nodeId is a UUID (production backend) or category path
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(nodeId);
      
      if (isUUID) {
        // It's a UUID, fetch from backend
        const skill = await skillService.getSkill(nodeId, {
          include_children: true,
          include_parent: true
        });
        setSelectedSkill(skill);
        if (onSkillSelect) {
          onSkillSelect(skill);
        }
      } else {
        // It's a category path - find the node in the tree and construct skill from it
        const findNodeInTree = (nodes: SkillTreeNode[], id: string): SkillTreeNode | null => {
          for (const node of nodes) {
            if (node.id === id) {
              return node;
            }
            if (node.children && node.children.length > 0) {
              const found = findNodeInTree(node.children, id);
              if (found) return found;
            }
          }
          return null;
        };

        const node = findNodeInTree(skillTree, nodeId);
        if (node) {
          // Construct SkillParameter from node data
          const skill: SkillParameter = {
            id: node.id,
            name: node.name,
            description: node.description,
            parent_ref: node.parent_ref,
            skill_level: node.skill_level,
            is_deleted: false,
            created_at: '',
            updated_at: ''
          };
          setSelectedSkill(skill);
          if (onSkillSelect) {
            onSkillSelect(skill);
          }
        } else {
          console.warn('Node not found in tree:', nodeId);
        }
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to load skill details';
      setError(`Failed to load skill details: ${errorMessage}`);
      console.error('Error loading skill details:', err);
    }
  };

  const handleToggleExpand = (nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.name.trim()) {
      setError('Skill name is required');
      return;
    }

    try {
      setError(null);
      await skillService.createSkill(formData);
      setIsCreating(false);
      setFormData({ name: '', description: '', parent_ref: undefined });
      loadSkillTree();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to create skill';
      setError(`Failed to create skill: ${errorMessage}`);
      console.error('Error creating skill:', err);
    }
  };

  const handleUpdate = async () => {
    if (!selectedSkill) return;
    
    try {
      const updateData: SkillParameterUpdate = {
        name: formData.name,
        description: formData.description,
        parent_ref: formData.parent_ref
      };
      
      await skillService.updateSkill(selectedSkill.id, updateData);
      setIsEditing(false);
      loadSkillTree();
      
      // Reload selected skill
      const updated = await skillService.getSkill(selectedSkill.id, {
        include_children: true,
        include_parent: true
      });
      setSelectedSkill(updated);
    } catch (err) {
      setError('Failed to update skill');
      console.error('Error updating skill:', err);
    }
  };

  const handleDelete = async () => {
    if (!selectedSkill) return;
    
    Modal.confirm({
      title: 'Delete Skill',
      content: `Are you sure you want to delete "${selectedSkill.name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await skillService.deleteSkill(selectedSkill.id);
          setSelectedSkill(null);
          loadSkillTree();
        } catch (err) {
          setError('Failed to delete skill. It may have children.');
          console.error('Error deleting skill:', err);
        }
      },
    });
  };

  const startEdit = () => {
    if (!selectedSkill) return;
    
    setFormData({
      name: selectedSkill.name,
      description: selectedSkill.description || '',
      parent_ref: selectedSkill.parent_ref
    });
    setIsEditing(true);
  };

  const startCreate = (parentId?: string) => {
    setFormData({
      name: '',
      description: '',
      parent_ref: parentId
    });
    setIsCreating(true);
  };

  const handleImport = async () => {
    if (!importJson.trim()) {
      setError('Please provide JSON data to import');
      return;
    }

    setImportLoading(true);
    setError(null);
    setImportResult(null);

    try {
      let jsonData;
      try {
        jsonData = JSON.parse(importJson);
      } catch (parseError) {
        setError('Invalid JSON format. Please check your JSON syntax.');
        setImportLoading(false);
        return;
      }

      const result = await skillService.importSkills({
        json_data: jsonData,
        overwrite_existing: overwriteExisting
      });

      setImportResult(result);
      
      // Reload the skill tree after successful import
      await loadSkillTree();
      
      // Clear the JSON input after successful import
      setImportJson('');
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to import skills';
      setError(`Import failed: ${errorMessage}`);
      console.error('Error importing skills:', err);
    } finally {
      setImportLoading(false);
    }
  };

  const renderSkillNode = (node: SkillTreeNode, depth: number = 0): React.ReactNode => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children && node.children.length > 0;
    const isSelected = selectedSkill?.id === node.id;

    // Simple search filter
    if (searchTerm && !node.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return null;
    }

    return (
      <div key={node.id} className={styles.skillNode}>
          <div
            className={`${styles.skillNodeContent} ${isSelected ? styles.selected : ''}`}
          style={{ paddingLeft: `${depth * 20}px` }}
        >
          {hasChildren && (
            <button
              className={styles.expandButton}
              onClick={() => handleToggleExpand(node.id)}
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          )}
          {!hasChildren && <span className={styles.expandPlaceholder}></span>}
          
          <span
            className={styles.skillName}
            onClick={() => handleNodeClick(node.id)}
          >
            {node.name}
          </span>
          
          <span className={styles.skillLevelBadge}>L{node.skill_level}</span>
          
          <button
            className={styles.addChildButton}
            onClick={() => startCreate(node.id)}
            title="Add child skill"
          >
            +
          </button>
        </div>
        
        {isExpanded && hasChildren && (
          <div className={styles.skillChildren}>
            {(node.children || []).map((child: SkillTreeNode) => renderSkillNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.skillManager}>
      <div className={styles.skillManagerHeader}>
        <h2>Skill Taxonomy</h2>
        <div className={styles.skillManagerActions}>
          <input
            type="text"
            className="search-input"
            placeholder="Search skills..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            className="btn btn-secondary"
            onClick={() => setShowImport(!showImport)}
          >
            {showImport ? 'Hide' : 'Bulk Import'}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => startCreate()}
          >
            + New Root Skill
          </button>
        </div>
      </div>

      {showImport && (
        <div className="import-panel" style={{ 
          margin: '1rem 0', 
          padding: '1rem', 
          border: '1px solid #ddd', 
          borderRadius: '4px',
          backgroundColor: '#f9f9f9'
        }}>
          <h3>Bulk Import Skills from JSON</h3>
          <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '1rem' }}>
            Paste JSON data in the format expected by the import endpoint. 
            The JSON should contain skill hierarchy data (e.g., SkillCategories array).
          </p>
          
          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label>
              <input
                type="checkbox"
                checked={overwriteExisting}
                onChange={(e) => setOverwriteExisting(e.target.checked)}
                style={{ marginRight: '0.5rem' }}
              />
              Overwrite existing skills
            </label>
          </div>

          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label>JSON Data *</label>
            <textarea
              value={importJson}
              onChange={(e) => setImportJson(e.target.value)}
              placeholder='{"SkillCategories": [...]}'
              rows={10}
              style={{
                width: '100%',
                fontFamily: 'monospace',
                fontSize: '0.9em',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div className="form-actions">
            <button
              className="btn btn-primary"
              onClick={handleImport}
              disabled={importLoading || !importJson.trim()}
            >
              {importLoading ? 'Importing...' : 'Import Skills'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowImport(false);
                setImportJson('');
                setImportResult(null);
                setError(null);
              }}
            >
              Cancel
            </button>
          </div>

          {importResult && (
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              backgroundColor: '#e8f5e9',
              border: '1px solid #4caf50',
              borderRadius: '4px'
            }}>
              <h4 style={{ marginTop: 0 }}>Import Results</h4>
              {importResult.statistics ? (
                <div>
                  <p><strong>Total Processed:</strong> {importResult.statistics.total_processed}</p>
                  <p><strong>Created:</strong> {importResult.statistics.created}</p>
                  <p><strong>Updated:</strong> {importResult.statistics.updated}</p>
                  <p><strong>Skipped:</strong> {importResult.statistics.skipped}</p>
                  {importResult.statistics.errors && importResult.statistics.errors.length > 0 && (
                    <div>
                      <p><strong>Errors:</strong></p>
                      <ul>
                        {importResult.statistics.errors.map((err, idx) => (
                          <li key={idx}>{err.skill_name}: {err.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {importResult.imported !== undefined && <p><strong>Imported:</strong> {importResult.imported}</p>}
                  {importResult.updated !== undefined && <p><strong>Updated:</strong> {importResult.updated}</p>}
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div>
                      <p><strong>Errors:</strong></p>
                      <ul>
                        {importResult.errors.map((err, idx) => (
                          <li key={idx}>{err.skill_name}: {err.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {importResult.message && <p><strong>Message:</strong> {importResult.message}</p>}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className={styles.skillManagerContent}>
        <div className={styles.skillTreePanel}>
          {loading ? (
            <div className="loading">Loading skill tree...</div>
          ) : (
            <div className={styles.skillTree}>
              {(skillTree || []).map(node => renderSkillNode(node))}
            </div>
          )}
        </div>

        <div className={styles.skillDetailsPanel}>
          {isCreating && (
            <div className={styles.skillForm}>
              <h3>Create New Skill</h3>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="form-actions">
                <button className="btn btn-primary" onClick={handleCreate}>
                  Create
                </button>
                <button className="btn btn-secondary" onClick={() => setIsCreating(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {isEditing && selectedSkill && (
            <div className={styles.skillForm}>
              <h3>Edit Skill</h3>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="form-actions">
                <button className="btn btn-primary" onClick={handleUpdate}>
                  Save
                </button>
                <button className="btn btn-secondary" onClick={() => setIsEditing(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!isCreating && !isEditing && selectedSkill && (
            <div className={styles.skillDetails}>
              <h3>{selectedSkill.name}</h3>
              <div className={styles.skillInfo}>
                <div className="info-row">
                  <label>Level:</label>
                  <span>{selectedSkill.skill_level}</span>
                </div>
                {selectedSkill.description && (
                  <div className="info-row">
                    <label>Description:</label>
                    <p>{selectedSkill.description}</p>
                  </div>
                )}
                {selectedSkill.parent && (
                  <div className="info-row">
                    <label>Parent:</label>
                    <span>{selectedSkill.parent.name}</span>
                  </div>
                )}
                {selectedSkill.children && selectedSkill.children.length > 0 && (
                  <div className="info-row">
                    <label>Children:</label>
                    <ul>
                      {selectedSkill.children.map((child: { id: string; name: string; skill_level: number }) => (
                        <li key={child.id}>{child.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className={styles.skillActions}>
                <button className="btn btn-primary" onClick={startEdit}>
                  Edit
                </button>
                <button className="btn btn-danger" onClick={handleDelete}>
                  Delete
                </button>
              </div>
            </div>
          )}

          {!isCreating && !isEditing && !selectedSkill && (
            <div className="no-selection">
              <p>Select a skill to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

